import * as Popover from '@radix-ui/react-popover'
import { CaretUp, DotsSixVertical } from '@phosphor-icons/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowCounterClockwise, ArrowDown, ArrowUp, CaretDoubleLeft, CaretDown, CaretDoubleRight, CaretUpDown, ChevronLeft, ChevronRight, Columns3, LockSimple, Search, X } from '@/design-system/icons'
import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { formatMoney, formatNumber, useT, type Localized } from '@/i18n'
import { formatDateTime } from '@/domain/dates'
import { useUi } from '@/stores/ui'
import { Checkbox, Select } from './forms'
import { Button, cn, IconButton, type IconComponent } from './primitives'
import { ErrorState, FilteredEmptyState, TableSkeleton } from './states'
import { isNameKey, isStatusKey, smartCell } from './cells'

export type ColumnType = 'text' | 'number' | 'money' | 'date' | 'datetime' | 'mono' | 'bool'

export interface Column<T> {
  id: string
  header: Localized | string
  accessor: (row: T) => unknown
  cell?: (row: T) => ReactNode
  type?: ColumnType
  width?: number
  minWidth?: number
  align?: 'start' | 'end' | 'center'
  sortable?: boolean
  hiddenByDefault?: boolean
  /** Always visible, cannot be hidden by the user (e.g. AWB). */
  pinned?: boolean
  /** Section of the column picker this column is listed under (a `ColumnGroup.id`). Ungrouped columns come first. */
  group?: string
  /** One-line definition shown in the column picker and as the header tooltip. */
  description?: Localized | string
}

/** A titled section of the column picker. */
export interface ColumnGroup {
  id: string
  title: Localized | string
  description?: Localized | string
}

export interface BulkAction<T> {
  label: string
  icon?: IconComponent
  onRun: (rows: T[]) => void
  danger?: boolean
  hidden?: boolean
}

export interface DataTableProps<T> {
  /** Stable id used to persist column visibility. */
  tableId: string
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T, index: number) => string
  loading?: boolean
  fetching?: boolean
  error?: unknown
  onRetry?: () => void
  /** Server pagination. `total` may be unknown — then paging relies on page fullness. */
  page?: number
  pageSize?: number
  total?: number | null
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
  /** Sorting is applied to the loaded page unless `onSortChange` handles it server-side. */
  sort?: { id: string; dir: 'asc' | 'desc' } | null
  onSortChange?: (sort: { id: string; dir: 'asc' | 'desc' } | null) => void
  selectable?: boolean
  bulkActions?: BulkAction<T>[]
  /**
   * Ctrl+C with rows ticked: handed the ticked rows (the page decides what a row copies as — the AWB, usually).
   * Only when nothing else is being copied: a text selection or a focused field keeps the browser's own copy.
   */
  onCopySelection?: (rows: T[]) => void
  onRowClick?: (row: T) => void
  rowActions?: (row: T) => ReactNode
  /** Phone layout (< 640px): one card per row instead of the grid. Omit for an automatic card built from the visible columns. */
  mobileCard?: (row: T) => ReactNode
  emptyState?: ReactNode
  onClearFilters?: () => void
  toolbar?: ReactNode
  footer?: ReactNode
  /** Sections for the column picker (columns carry `group`). Without it the picker is one flat list. */
  columnGroups?: ColumnGroup[]
  /** Show the column picker in the toolbar row (default true). Pass false when the page renders its own `ColumnPicker`. */
  columnPicker?: boolean
  /** Controlled column visibility (ids of hidden columns) - pair with `useColumnVisibility` to share it with a page-level picker. */
  hiddenColumns?: string[]
  onHiddenColumnsChange?: (hidden: string[]) => void
  /** Controlled column order (ids; see `orderColumns`). Omitted = the table keeps its own persisted order. */
  columnOrder?: string[]
  onColumnOrderChange?: (order: string[]) => void
  /** Height strategy: `fill` grows to the parent (flex), `auto` caps at a viewport-based height. */
  height?: 'fill' | 'auto'
  className?: string
}

const DEFAULT_WIDTH: Record<ColumnType, number> = { text: 180, number: 110, money: 120, date: 120, datetime: 160, mono: 150, bool: 90 }

const storageKey = (tableId: string) => `aos-cols:${tableId}`
const defaultHidden = (columns: Column<unknown>[]) => columns.filter((c) => c.hiddenByDefault && !c.pinned).map((c) => c.id)

interface StoredCols { v: 2; hidden: string[]; known: string[]; order?: string[] }

function readStored(tableId: string): string[] | StoredCols | null {
  try {
    const raw = localStorage.getItem(storageKey(tableId))
    return raw ? (JSON.parse(raw) as string[] | StoredCols) : null
  } catch {
    return null
  }
}

/**
 * Stored as `{ v: 2, hidden, known, order }` (legacy: a bare array of hidden ids). `known` lets a column added after
 * the user saved their choice start with its own default instead of silently showing.
 */
function readHidden(tableId: string, columns: Column<unknown>[]): string[] {
  const parsed = readStored(tableId)
  if (Array.isArray(parsed)) return parsed
  if (parsed && Array.isArray(parsed.hidden)) {
    const known = new Set(parsed.known ?? [])
    return [...parsed.hidden, ...columns.filter((c) => c.hiddenByDefault && !known.has(c.id)).map((c) => c.id)]
  }
  return defaultHidden(columns)
}

/**
 * The sort a person chose, kept per table.
 *
 * The office sorts a long list, opens one shipment and comes back — and used to find it unsorted again every single
 * time (owner, 2026-09-27: "لما بنرجع تاني لازم نرتب من اول وجديد"). A browser that refuses storage just sorts from
 * scratch, as before.
 */
type SortState = { id: string; dir: 'asc' | 'desc' } | null
const sortKey = (tableId: string) => `aos-sort:${tableId}`

function readSort(tableId: string): SortState {
  try {
    const raw = localStorage.getItem(sortKey(tableId))
    const parsed = raw ? (JSON.parse(raw) as SortState) : null
    return parsed && typeof parsed.id === 'string' && (parsed.dir === 'asc' || parsed.dir === 'desc') ? parsed : null
  } catch {
    return null
  }
}

function writeSort(tableId: string, sort: SortState) {
  try {
    if (sort) localStorage.setItem(sortKey(tableId), JSON.stringify(sort))
    else localStorage.removeItem(sortKey(tableId))
  } catch {
    /* storage unavailable */
  }
}

function readOrder(tableId: string): string[] {
  const parsed = readStored(tableId)
  return parsed && !Array.isArray(parsed) && Array.isArray(parsed.order) ? parsed.order : []
}

function writeStored(tableId: string, columns: Column<unknown>[], hidden: string[], order: string[]) {
  try {
    localStorage.setItem(storageKey(tableId), JSON.stringify({ v: 2, hidden, known: columns.map((c) => c.id), order } satisfies StoredCols))
  } catch {
    /* storage unavailable */
  }
}

/**
 * Columns in the user's order: ids from `order` first in that order; a column the order does not know keeps its
 * place right after its default predecessor; pinned columns always lead. An empty order is the default order.
 */
export function orderColumns<C extends { id: string; pinned?: boolean }>(columns: C[], order: string[] | undefined): C[] {
  if (!order?.length) return columns
  const pos = new Map(order.map((id, i) => [id, i]))
  let last = -1
  const rank = new Map<string, number>()
  columns.forEach((c, i) => {
    const r = pos.get(c.id)
    last = r !== undefined ? r : last + 0.001 * (i + 1) / (columns.length + 1)
    rank.set(c.id, last)
  })
  return [...columns].sort((a, b) => Number(Boolean(b.pinned)) - Number(Boolean(a.pinned)) || rank.get(a.id)! - rank.get(b.id)!)
}

/**
 * The columns a table is showing right now - the user's saved order with their hidden columns dropped. Used by the
 * export button so a file carries exactly what is on screen, without the page having to lift the table state.
 */
export function visibleColumnsFor<T>(tableId: string, columns: Column<T>[], hidden?: string[], order?: string[]): Column<T>[] {
  const cols = columns as Column<unknown>[]
  const off = hidden ?? readHidden(tableId, cols)
  return orderColumns(columns, order ?? readOrder(tableId)).filter((c) => c.pinned || !off.includes(c.id))
}

/**
 * Column visibility and order persisted per `tableId` - share it between a page-level `ColumnPicker` and `DataTable`
 * (`hiddenColumns` / `columnOrder`). `reset` restores both defaults.
 */
export function useColumnVisibility<T>(tableId: string, columns: Column<T>[], persist = true) {
  const cols = columns as Column<unknown>[]
  const [hidden, setHidden] = useState<string[]>(() => readHidden(tableId, cols))
  const [order, setOrder] = useState<string[]>(() => readOrder(tableId))
  const ids = columns.map((c) => c.id).join('|')
  useEffect(() => { if (persist) writeStored(tableId, cols, hidden, order) }, [hidden, order, tableId, ids, persist]) // eslint-disable-line react-hooks/exhaustive-deps
  const reset = useCallback(() => { setHidden(defaultHidden(cols)); setOrder([]) }, [ids]) // eslint-disable-line react-hooks/exhaustive-deps
  return { hidden, setHidden, order, setOrder, reset }
}

/**
 * A header reads on one line (owner, 2026-10-21: «عمولة التوصيل» wrapped and clipped), so a column is never narrower
 * than its whole label: about 6.6px a character at the 12px header size, plus the cell padding and the sort arrow.
 */
const HEADER_CHROME = 24 + 18
export function headerMinWidth(label: string) {
  return Math.ceil(label.trim().length * 6.6) + HEADER_CHROME
}

/** A code column (AWB, id, number) is never cut: as wide as its longest value, about 8px a character in mono. */
const CODE_ID = /(^|[a-z_])(awb|code|id|no|number|serial|ref|trans)$|^(awb|id|code)/i
const isCodeColumn = (c: { id: string; type?: ColumnType }) => c.type === 'mono' || CODE_ID.test(c.id)

function compare(a: unknown, b: unknown) {
  if (a == null) return b == null ? 0 : 1
  if (b == null) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/** Every row between `a` and `b` (either order, both included) set to `value`; the rest as they were. */
export function rangeSelection(prev: ReadonlySet<string>, keys: readonly string[], a: number, b: number, value: boolean): Set<string> {
  const next = new Set(prev)
  for (let i = Math.min(a, b); i <= Math.max(a, b); i++) {
    const k = keys[i]
    if (k === undefined) continue
    if (value) next.add(k)
    else next.delete(k)
  }
  return next
}

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    tableId, columns, rows, rowKey, loading, fetching, error, onRetry, page = 1, pageSize = 50, total, onPageChange, onPageSizeChange,
    pageSizeOptions = [25, 50, 100, 200], sort: sortProp, onSortChange, selectable, bulkActions, onCopySelection, onRowClick, rowActions, mobileCard, emptyState,
    onClearFilters, toolbar, footer, height = 'fill', className, columnGroups, columnPicker = true, hiddenColumns, onHiddenColumnsChange, columnOrder, onColumnOrderChange,
  } = props
  const { t, l, locale } = useT()
  const density = useUi((s) => s.density)
  // the portal's calm rows: at least 52px, 44 compact (owner, 2026-10-20: «حاسس ان عيني مزغلله»); a row grows to its content
  const rowHeight = density === 'compact' ? 44 : 52
  // Phones get a card per row: a 10-column grid scrolled sideways is unusable at 360px.
  const phone = useIsPhone()

  const own = useColumnVisibility(tableId, columns, hiddenColumns === undefined && columnOrder === undefined)
  const hidden = hiddenColumns ?? own.hidden
  const ownSet = own.setHidden
  const setHidden = useCallback((next: string[]) => (onHiddenColumnsChange ? onHiddenColumnsChange(next) : ownSet(next)), [onHiddenColumnsChange, ownSet])
  const order = columnOrder ?? own.order
  const ownOrder = own.setOrder
  const setOrder = useCallback((next: string[]) => (onColumnOrderChange ? onColumnOrderChange(next) : ownOrder(next)), [onColumnOrderChange, ownOrder])
  const orderedColumns = useMemo(() => orderColumns(columns, order), [columns, order])

  // the chosen sort is remembered per table (see readSort): coming back from a shipment keeps the order
  const [localSort, setLocalSort] = useState<SortState>(() => readSort(tableId))
  const sort = onSortChange ? sortProp ?? null : localSort
  const setSort = useCallback((next: SortState) => {
    if (onSortChange) return onSortChange(next)
    writeSort(tableId, next)
    setLocalSort(next)
  }, [onSortChange, tableId])

  const visibleColumns = useMemo(() => orderedColumns.filter((c) => c.pinned || !hidden.includes(c.id)), [orderedColumns, hidden])

  const sortedRows = useMemo(() => {
    if (!rows || !sort || onSortChange) return rows ?? []
    const col = columns.find((c) => c.id === sort.id)
    if (!col) return rows
    const out = [...rows].sort((a, b) => compare(col.accessor(a), col.accessor(b)))
    return sort.dir === 'desc' ? out.reverse() : out
  }, [rows, sort, columns, onSortChange])

  const [selected, setSelected] = useState<Set<string>>(new Set())
  useEffect(() => setSelected(new Set()), [rows])
  const keys = useMemo(() => sortedRows.map((r, i) => rowKey(r, i)), [sortedRows, rowKey])
  const allSelected = keys.length > 0 && keys.every((k) => selected.has(k))
  const someSelected = !allSelected && keys.some((k) => selected.has(k))
  const selectedRows = useMemo(() => sortedRows.filter((_, i) => selected.has(keys[i]!)), [sortedRows, keys, selected])

  /*
   * Ticking many rows the way a spreadsheet does (owner, 2026-09-28: "نعمل تحديد بالماوس عليهم مرة واحدة ونعمل
   * Ctrl+C"): press on a box and drag down the column — every row passed takes the first box's new state; Shift+click
   * ticks the whole run from the last box clicked; and Ctrl+C copies what is ticked.
   */
  const lastClicked = useRef<number | null>(null)
  const drag = useRef<{ value: boolean; from: number } | null>(null)
  useEffect(() => {
    const end = () => { drag.current = null }
    window.addEventListener('pointerup', end)
    return () => window.removeEventListener('pointerup', end)
  }, [])
  const boxDown = useCallback((index: number) => {
    drag.current = { value: !selected.has(keys[index]!), from: index }
  }, [selected, keys])
  const boxEnter = useCallback((index: number) => {
    const d = drag.current
    if (!d || index === d.from) return
    setSelected((prev) => rangeSelection(prev, keys, d.from, index, d.value))
  }, [keys])
  const boxClickCapture = useCallback((index: number, e: { shiftKey: boolean; preventDefault(): void; stopPropagation(): void }) => {
    const last = lastClicked.current
    lastClicked.current = index
    if (!e.shiftKey || last === null || last === index) return
    e.preventDefault()
    e.stopPropagation()
    const value = !selected.has(keys[index]!)
    setSelected((prev) => rangeSelection(prev, keys, last, index, value))
  }, [selected, keys])
  useEffect(() => {
    if (!onCopySelection || !selectedRows.length) return
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'c') return
      // the key may come from the page itself (document), not an element — only a field keeps its own copy
      const target = e.target
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]')) return
      if (String(window.getSelection?.() ?? '').trim()) return
      e.preventDefault()
      onCopySelection(selectedRows)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCopySelection, selectedRows])

  // the longest value of each code column in what is loaded (a sample), so «QUK66836…» is never cut
  const codeWidth = useMemo(() => {
    const out = new Map<string, number>()
    const sample = (rows ?? []).slice(0, 400)
    for (const c of columns) {
      if (c.cell || !isCodeColumn(c)) continue
      let longest = 0
      for (const r of sample) { const v = c.accessor(r); if (typeof v === 'string' || typeof v === 'number') longest = Math.max(longest, String(v).length) }
      if (longest) out.set(c.id, Math.min(Math.ceil(longest * 8.2) + 34, 260))
    }
    return out
  }, [rows, columns])
  const colMin = useCallback((c: Column<T>) => Math.max(c.minWidth ?? Math.min(c.width ?? DEFAULT_WIDTH[c.type ?? 'text'], 140), headerMinWidth(l(c.header)), codeWidth.get(c.id) ?? 0), [l, codeWidth])
  const template = useMemo(() => {
    const parts = visibleColumns.map((c) => {
      const min = colMin(c)
      return `minmax(${min}px, ${Math.max(c.width ?? DEFAULT_WIDTH[c.type ?? 'text'], min)}fr)`
    })
    if (selectable) parts.unshift('44px')
    if (rowActions) parts.push('56px')
    return parts.join(' ')
  }, [visibleColumns, selectable, rowActions, colMin])
  const minWidth = useMemo(
    // + the rows' inset (8px each side) so the header and the rows line up
    () => visibleColumns.reduce((s, c) => s + colMin(c), 0) + (selectable ? 44 : 0) + (rowActions ? 56 : 0) + 16,
    [visibleColumns, selectable, rowActions, colMin],
  )

  const scrollRef = useRef<HTMLDivElement>(null)
  /*
   * Rows are measured, not fixed: a two-line cell made a fixed-height row spill over the next one (owner, 2026-10-21:
   * «اي القرف ده» on الاستلامات). Separate rounded rows, 6px apart, like the portal's web list.
   */
  const virtualizer = useVirtualizer({ count: sortedRows.length, getScrollElement: () => scrollRef.current, estimateSize: () => rowHeight + 6, gap: 6, overscan: 12, paddingStart: 8, paddingEnd: 8 })
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [page])

  const nameColumnId = useMemo(() => visibleColumns.find((c) => !c.cell && isNameKey(c.id))?.id, [visibleColumns])

  const renderCell = useCallback(
    (col: Column<T>, row: T, bare = false): ReactNode => {
      if (col.cell) return col.cell(row)
      const v = col.accessor(row)
      // an empty cell is a light dash in the table; a phone card leaves the line out altogether (AutoCard)
      if (v === null || v === undefined || v === '') return <span className="text-fg-subtle/50">—</span>
      // a bare true / false is never shown as the word: «نعم» / «لا» (a «false» headed every card of إدارة التحصيل)
      if (typeof v === 'boolean' && !col.type) return v ? t('common.yes') : t('common.no')
      const smart = smartCell(col.id, v, col.type ?? 'text', locale, col.id === nameColumnId, bare)
      if (smart !== null) return smart
      switch (col.type) {
        case 'number':
          return formatNumber(Number(v), locale)
        case 'money':
          return formatMoney(Number(v), locale)
        case 'date':
          return formatDateTime(v as string, locale, false)
        case 'datetime':
          return compactDateTime(v as string, locale)
        case 'bool':
          return v ? t('common.yes') : t('common.no')
        default:
          return <span className="dt-text" dir="auto">{String(v)}</span>
      }
    },
    [locale, t, nameColumnId],
  )

  const hasMore = total != null ? page * pageSize < total : (rows?.length ?? 0) >= pageSize
  const pageCount = total != null ? Math.max(1, Math.ceil(total / pageSize)) : null
  const showPagination = Boolean(onPageChange) && (page > 1 || hasMore || (total ?? 0) > 0)

  let body: ReactNode
  if (loading) body = <TableSkeleton columns={Math.min(visibleColumns.length, 7)} />
  else if (error) body = <ErrorState error={error} onRetry={onRetry} />
  else if (sortedRows.length === 0) body = emptyState ?? <FilteredEmptyState onClear={onClearFilters} />
  else if (phone)
    body = (
      <ul role="rowgroup" className="row-in space-y-3 bg-bg-subtle p-3" key={`${page}-${sortedRows.length}`}>
        {sortedRows.map((row, i) => {
          const key = keys[i]!
          const isSelected = selected.has(key)
          return (
            <li
              key={key}
              role="row"
              aria-selected={isSelected || undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(/* one card per record on a soft grey ground, so each client stands apart (owner, 2026-10-20: «الفاصل بين كل عميل وعميل الموضوع مش مريح لاعين») */ 'flex items-start gap-2.5 rounded-2xl border border-border bg-surface p-4 text-sm shadow-[0_1px_2px_rgba(16,24,40,0.06)] transition-colors', isSelected ? 'border-accent bg-accent-soft/60' : 'hover:border-border-strong', onRowClick && 'cursor-pointer active:bg-bg-subtle')}
            >
              {selectable && (
                <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(v) =>
                      setSelected((prev) => {
                        const next = new Set(prev)
                        if (v) next.add(key)
                        else next.delete(key)
                        return next
                      })
                    }
                  />
                </div>
              )}
              <div className="min-w-0 flex-1">{mobileCard ? mobileCard(row) : <AutoCard columns={visibleColumns} row={row} render={renderCell} label={l} />}</div>
              {rowActions ? (
                <div className="-me-1 flex shrink-0 items-center self-center" onClick={(e) => e.stopPropagation()}>
                  {rowActions(row)}
                </div>
              ) : (
                onRowClick && (
                  <span className="flex size-8 shrink-0 items-center justify-center self-center rounded-full bg-bg-subtle text-fg-subtle" aria-hidden>
                    {locale === 'ar' ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
                  </span>
                )
              )}
            </li>
          )
        })}
      </ul>
    )
  else
    body = (
      <div role="rowgroup" className="row-in dt-rows bg-bg-subtle/50" key={`${page}-${sortedRows.length}`} style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map((vr) => {
          const row = sortedRows[vr.index]!
          const key = keys[vr.index]!
          return (
            <TableRow
              key={key}
              index={vr.index}
              measure={virtualizer.measureElement}
              top={vr.start}
              height={rowHeight}
              template={template}
              selected={selected.has(key)}
              clickable={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {selectable && (
                <div
                  className="flex h-full min-h-10 cursor-pointer select-none items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                  onClickCapture={(e) => boxClickCapture(vr.index, e)}
                  onPointerDown={() => boxDown(vr.index)}
                  onPointerEnter={() => boxEnter(vr.index)}
                  title={locale === 'ar' ? 'اسحب لتحديد أكثر من صف — Shift لتحديد نطاق — Ctrl+C للنسخ' : 'Drag to tick many — Shift for a range — Ctrl+C to copy'}
                >
                  <Checkbox
                    checked={selected.has(key)}
                    onChange={(v) =>
                      setSelected((prev) => {
                        const next = new Set(prev)
                        if (v) next.add(key)
                        else next.delete(key)
                        return next
                      })
                    }
                  />
                </div>
              )}
              {visibleColumns.map((col) => (
                <div
                  key={col.id}
                  role="cell"
                  // the whole value on hover, wherever the column is too narrow for it
                  title={(() => { const v = col.accessor(row); return typeof v === 'string' || typeof v === 'number' ? String(v) : undefined })()}
                  className={cn(
                    'dt-cell min-w-0 px-3 py-2 text-fg',
                    (col.type === 'number' || col.type === 'money') && 'tabular',
                    col.align === 'end' || col.type === 'number' || col.type === 'money' ? 'text-end' : col.align === 'center' ? 'text-center' : 'text-start',
                  )}
                >
                  {renderCell(col, row)}
                </div>
              ))}
              {rowActions && (
                <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                  {rowActions(row)}
                </div>
              )}
            </TableRow>
          )
        })}
      </div>
    )

  return (
    <div className={cn('flex min-h-0 flex-col overflow-hidden rounded-[18px] border border-border/70 bg-surface shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-16px_rgba(16,24,40,0.16)]', height === 'fill' ? 'min-h-[380px] flex-1' : /* a table sized by its rows never shrinks inside a full-height page: it went to zero there on the phone («بفتح مفيش داتا بتظهر والشاشة ثابتة», 2026-10-21) */ 'shrink-0', className)}>
      {(toolbar || selectedRows.length > 0) && (
        <div className="flex min-h-12 flex-wrap items-center gap-2 border-b border-border px-3 py-2">
          {selectedRows.length > 0 && bulkActions?.length ? (
            <div className="flex flex-1 flex-wrap items-center gap-2 animate-fade-in">
              <span className="text-sm font-medium">{t('common.selected', { count: selectedRows.length })}</span>
              <IconButton size="xs" label={t('common.clear')} icon={X} onClick={() => setSelected(new Set())} />
              <div className="mx-1 h-5 w-px bg-border" />
              {bulkActions
                .filter((a) => !a.hidden)
                .map((a) => (
                  <Button key={a.label} size="sm" variant={a.danger ? 'danger' : 'secondary'} icon={a.icon} onClick={() => a.onRun(selectedRows)}>
                    {a.label}
                  </Button>
                ))}
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{toolbar}</div>
          )}
          {columnPicker && <ColumnPicker columns={columns} groups={columnGroups} hidden={hidden} onChange={setHidden} order={order} onOrderChange={setOrder} compact />}
        </div>
      )}

      <div ref={scrollRef} className={cn('relative min-h-0 overflow-auto', height === 'fill' ? 'flex-1' : 'max-h-[min(70dvh,720px)]')} role="table" aria-rowcount={sortedRows.length} aria-busy={fetching || loading}>
        <div style={phone ? undefined : { minWidth }}>
          {!phone && (
          <div role="row" className="sticky top-0 z-10 grid min-h-11 items-center border-b border-border/60 bg-bg-subtle/80 px-2 py-1 text-[12px] font-semibold text-fg-muted backdrop-blur" style={{ gridTemplateColumns: template }}>
            {selectable && (
              <div className="flex items-center justify-center">
                <Checkbox checked={allSelected} indeterminate={someSelected} onChange={(v) => setSelected(v ? new Set(keys) : new Set())} disabled={!sortedRows.length} />
              </div>
            )}
            {visibleColumns.map((col) => {
              const active = sort?.id === col.id
              const sortable = col.sortable !== false
              return (
                <div key={col.id} role="columnheader" aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined} title={[l(col.header), l(col.description)].filter(Boolean).join(' - ')} className={cn('group/h flex min-w-0 items-center self-stretch px-3', col.type === 'number' || col.type === 'money' || col.align === 'end' ? 'justify-end text-end' : '')}>
                  <button
                    type="button"
                    disabled={!sortable}
                    onClick={() => setSort(!active ? { id: col.id, dir: 'asc' } : sort!.dir === 'asc' ? { id: col.id, dir: 'desc' } : null)}
                    className={cn('inline-flex max-w-full items-center gap-1 text-start', sortable && 'hover:text-fg', active && 'font-semibold text-fg')}
                  >
                    {/* the header is its words, on one line: the per-type glyphs («T» …) only cluttered it (owner, 2026-10-21) */}
                    <span className="min-w-0 truncate whitespace-nowrap leading-tight" dir="auto">{l(col.header)}</span>
                    {active ? (sort!.dir === 'asc' ? <ArrowUp size={11} className="shrink-0" aria-hidden /> : <ArrowDown size={11} className="shrink-0" aria-hidden />) : sortable ? <CaretUpDown size={11} className="shrink-0 opacity-0 transition-opacity group-hover/h:opacity-40" aria-hidden /> : null}
                  </button>
                </div>
              )
            })}
            {rowActions && <div />}
            {fetching && !loading && <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden"><div className="skeleton h-full w-full opacity-70" /></div>}
          </div>
          )}
          {phone && fetching && !loading && <div className="absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden"><div className="skeleton h-full w-full opacity-70" /></div>}
          {body}
        </div>
      </div>

      {(showPagination || footer) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm text-fg-muted sm:px-5 sm:py-3.5">
          <div className="flex items-center gap-3">
            {footer}
            {total != null && total > 0 && (
              <span className="tabular">
                {locale === 'ar'
                  ? `عرض ${formatNumber((page - 1) * pageSize + 1, locale)} إلى ${formatNumber(Math.min(page * pageSize, total), locale)} من ${formatNumber(total, locale)}`
                  : `Showing ${formatNumber((page - 1) * pageSize + 1, locale)} to ${formatNumber(Math.min(page * pageSize, total), locale)} of ${formatNumber(total, locale)}`}
              </span>
            )}
          </div>
          {showPagination && (
            <div className="flex flex-wrap items-center gap-4">
              {onPageSizeChange && (
                <div className="flex items-center gap-2">
                  <span className="hidden text-xs sm:inline">{t('common.perPage')}</span>
                  <Select className="h-9 w-20 text-sm" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} options={pageSizeOptions.map((n) => ({ value: n, label: String(n) }))} />
                </div>
              )}
              <Pager page={page} pageCount={pageCount} hasMore={hasMore} disabled={Boolean(fetching)} rtl={locale === 'ar'} onChange={(p) => onPageChange?.(p)} labels={{ prev: t('common.previous'), next: t('common.next') }} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function pageList(page: number, count: number): (number | '…')[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1)
  const out: (number | '…')[] = [1]
  const from = Math.max(2, page - 1)
  const to = Math.min(count - 1, page + 1)
  if (from > 2) out.push('…')
  for (let i = from; i <= to; i++) out.push(i)
  if (to < count - 1) out.push('…')
  out.push(count)
  return out
}

function Pager({ page, pageCount, hasMore, disabled, rtl, onChange, labels }: { page: number; pageCount: number | null; hasMore: boolean; disabled: boolean; rtl: boolean; onChange: (p: number) => void; labels: { prev: string; next: string } }) {
  const btn = 'flex h-8 min-w-8 items-center justify-center rounded-lg border border-border/70 bg-surface px-2 text-[13px] font-medium text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg disabled:cursor-not-allowed disabled:opacity-40'
  const pages = pageCount ? pageList(page, pageCount) : [page]
  const last = pageCount ?? (hasMore ? page + 1 : page)
  return (
    <nav className="flex items-center gap-1.5" aria-label="Pagination">
      {pageCount && <button type="button" className={cn(btn, 'hidden sm:flex')} disabled={disabled || page <= 1} onClick={() => onChange(1)} aria-label="First">{rtl ? <CaretDoubleRight size={15} aria-hidden /> : <CaretDoubleLeft size={15} aria-hidden />}</button>}
      <button type="button" className={btn} disabled={disabled || page <= 1} onClick={() => onChange(page - 1)} aria-label={labels.prev}>{rtl ? <ChevronRight size={15} aria-hidden /> : <ChevronLeft size={15} aria-hidden />}</button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="hidden px-1 text-fg-subtle sm:inline">…</span>
        ) : (
          <button key={p} type="button" disabled={disabled} onClick={() => onChange(p)} aria-current={p === page ? 'page' : undefined} className={cn(btn, 'tabular', p === page ? /* the current page quiet, not a solid block (owner, 2026-10-21) */ '!border-brand/30 !bg-brand-soft font-bold !text-brand-text' : 'hidden sm:flex')}>
            {p}
          </button>
        ),
      )}
      {pageCount && <span className="tabular px-0.5 text-xs text-fg-muted sm:hidden">/ {pageCount}</span>}
      <button type="button" className={btn} disabled={disabled || !hasMore} onClick={() => onChange(page + 1)} aria-label={labels.next}>{rtl ? <ChevronLeft size={15} aria-hidden /> : <ChevronRight size={15} aria-hidden />}</button>
      {pageCount && <button type="button" className={cn(btn, 'hidden sm:flex')} disabled={disabled || page >= last} onClick={() => onChange(last)} aria-label="Last">{rtl ? <CaretDoubleLeft size={15} aria-hidden /> : <CaretDoubleRight size={15} aria-hidden />}</button>}
    </nav>
  )
}

const TableRow = memo(function TableRow({ index, measure, top, height, template, selected, clickable, onClick, children }: { index: number; measure: (el: Element | null) => void; top: number; height: number; template: string; selected: boolean; clickable: boolean; onClick?: () => void; children: ReactNode }) {
  return (
    <div
      ref={measure}
      data-index={index}
      role="row"
      aria-selected={selected || undefined}
      onClick={onClick}
      // a separate white rounded row, as tall as its content (the portal's web list)
      className={cn('dt-row absolute inset-x-2 grid items-center rounded-[14px] border bg-surface text-sm text-fg transition-[box-shadow,border-color,background-color]', selected ? 'border-accent bg-accent-soft/40' : 'border-border/70 hover:border-border hover:shadow-[0_2px_10px_rgba(16,24,40,0.07)]', clickable && 'cursor-pointer')}
      style={{ transform: `translateY(${top}px)`, minHeight: height, gridTemplateColumns: template }}
    >
      {children}
    </div>
  )
})

const PHONE_QUERY = '(max-width: 639px)'
const canMatch = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
/** True below Tailwind's `sm` breakpoint. Safe under jsdom (no matchMedia → never a phone). */
export function useIsPhone() {
  const subscribe = useCallback((cb: () => void) => {
    if (!canMatch()) return () => {}
    const m = window.matchMedia(PHONE_QUERY)
    m.addEventListener('change', cb)
    return () => m.removeEventListener('change', cb)
  }, [])
  return useSyncExternalStore(subscribe, () => (canMatch() ? window.matchMedia(PHONE_QUERY).matches : false), () => false)
}

/**
 * A date and time short enough for its column: «النهارده 9:42 م», «20 أكتوبر 9:42 م» this year, the year only when it
 * is another one. The long form cut off as «20 أكتوبر 2026، 9...» (owner, 2026-10-20).
 */
export function compactDateTime(v: string | number | Date, locale: 'ar' | 'en'): string {
  const d = v instanceof Date ? v : new Date(v)
  if (Number.isNaN(d.getTime())) return formatDateTime(v as string, locale, true)
  const loc = locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB'
  const tz = 'Africa/Cairo'
  const day = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: tz })
  const time = d.toLocaleTimeString(loc, { hour: 'numeric', minute: '2-digit', timeZone: tz })
  const now = new Date()
  if (day(d) === day(now)) return `${locale === 'ar' ? 'اليوم' : 'Today'} ${time}`
  const sameYear = day(d).slice(0, 4) === day(now).slice(0, 4)
  const date = d.toLocaleDateString(loc, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone: tz })
  return `${date} ${time}`
}
const isEmptyValue = (v: unknown) => v === null || v === undefined || v === ''
const isDateColumn = (c: { type?: ColumnType }) => c.type === 'date' || c.type === 'datetime'
const isFigureColumn = (c: { type?: ColumnType }) => c.type === 'money' || c.type === 'number'

/**
 * Phone fallback card. One record reads top to bottom: identity + status on the first line, its date as a quiet
 * line under it, the figures (money, counts) as small label + value facts, then the remaining facts as a list of label
 * on the start and value on the end (the portal's list card, owner 2026-10-20). Every value is rendered `bare`.
 */
function AutoCard<T>({ columns, row, render, label }: { columns: Column<T>[]; row: T; render: (col: Column<T>, row: T, bare?: boolean) => ReactNode; label: (h: Localized | string) => string }) {
  /*
   * The card's title: a column that names the record — a name, then a number like the operation or the waybill —
   * never a yes/no, a date or an amount. Taking the first column put «false» on top of every card (owner, 2026-10-20).
   */
  const titleable = (c: Column<T>) => { const v = c.accessor(row); return (typeof v === 'string' || typeof v === 'number') && !isEmptyValue(v) && !isDateColumn(c) && !isFigureColumn(c) }
  const lead = columns.find((c) => c.pinned && titleable(c)) ?? columns.find((c) => isNameKey(c.id) && titleable(c)) ?? columns.find((c) => /(^|[a-z])(id|no|number|code|awb|trans)/i.test(c.id) && titleable(c)) ?? columns.find(titleable) ?? columns[0]
  if (!lead) return null
  const rest = columns.filter((c) => c !== lead)
  const statusCol = rest.find((c) => isStatusKey(c.id) || /حالة|status/i.test(label(c.header)))
  const dateCol = rest.find((c) => c !== statusCol && isDateColumn(c))
  const taken = new Set([statusCol?.id, dateCol?.id].filter(Boolean) as string[])
  // a fact with nothing in it is left out, not printed as a dash: a card of «—» lines was the eye strain
  const filled = rest.filter((c) => !taken.has(c.id) && !isEmptyValue(c.accessor(row)))
  const figures = filled.filter((c) => isFigureColumn(c)).slice(0, 3)
  const figureIds = new Set(figures.map((c) => c.id))
  const facts = filled.filter((c) => !figureIds.has(c.id)).slice(0, 6)
  
  return (
    <div className="min-w-0">
      {/* the portal's list card: the record's name bold with its status on the end, the date quiet under it */}
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1 truncate text-[15px] font-bold leading-snug text-fg-strong">{render(lead, row, true)}</div>
        {statusCol && <div className="shrink-0">{render(statusCol, row)}</div>}
      </div>
      {dateCol && <div className="mt-0.5 truncate text-xs text-fg-subtle">{render(dateCol, row, true)}</div>}

      {figures.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {figures.map((col) => (
            <span key={col.id} className="inline-flex min-w-0 max-w-full items-baseline gap-1">
              <span className="truncate text-fg-muted">{label(col.header)}</span>
              <span className="tabular shrink-0 font-bold text-fg-strong">{render(col, row, true)}</span>
            </span>
          ))}
        </div>
      )}

      {facts.length > 0 && (
        <dl className="mt-3 grid gap-2">
          {facts.map((col) => (
            <div key={col.id} className="flex min-w-0 items-baseline justify-between gap-3">
              <dt className="shrink-0 text-xs text-fg-muted">{label(col.header)}</dt>
              <dd className="min-w-0 truncate text-end text-sm font-semibold text-fg-strong">{render(col, row, true)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
/* ------------------------------------------------------------ column picker */

/**
 * Searchable column picker: grouped sections (title + one-line description), locked columns, "Reset to defaults".
 * Used by every DataTable's toolbar, and exportable for a page that places it in its own toolbar.
 */
export function ColumnPicker<T>({ columns: defaults, groups, hidden, onChange, order, onOrderChange, compact, className }: {
  columns: Column<T>[]
  groups?: ColumnGroup[]
  hidden: string[]
  onChange: (hidden: string[]) => void
  /** With `onOrderChange`: rows get a drag handle and up/down buttons, and "Reset to defaults" also restores the order. */
  order?: string[]
  onOrderChange?: (order: string[]) => void
  compact?: boolean
  className?: string
}) {
  const { l, locale } = useT()
  const ar = locale === 'ar'
  const [q, setQ] = useState('')
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const columns = orderColumns(defaults, order)
  const reorderable = Boolean(onOrderChange)
  /** Moves `id` to where `target` is (same section), or one step up/down. */
  const moveTo = (id: string, target: string) => {
    if (!onOrderChange || id === target) return
    const ids = columns.map((c) => c.id).filter((x) => x !== id)
    const at = ids.indexOf(target)
    const from = columns.findIndex((c) => c.id === id)
    const to = columns.findIndex((c) => c.id === target)
    ids.splice(to > from ? at + 1 : at, 0, id)
    onOrderChange(ids)
  }
  const visible = columns.filter((c) => c.pinned || !hidden.includes(c.id)).length
  const needle = q.trim().toLowerCase()
  const match = (c: Column<T>) => !needle || l(c.header).toLowerCase().includes(needle) || l(c.description).toLowerCase().includes(needle)
  const sections: { id: string; title?: string; description?: string; cols: Column<T>[] }[] = []
  const ungrouped = columns.filter((c) => !c.group || !groups?.some((g) => g.id === c.group))
  if (ungrouped.length) sections.push({ id: '_', title: groups?.length ? (ar ? 'أعمدة عامة' : 'General') : undefined, cols: ungrouped })
  for (const g of groups ?? []) {
    const cols = columns.filter((c) => c.group === g.id)
    if (cols.length) sections.push({ id: g.id, title: l(g.title), description: l(g.description), cols })
  }
  const toggle = (id: string, on: boolean) => onChange(on ? hidden.filter((x) => x !== id) : [...new Set([...hidden, id])])
  const setSection = (cols: Column<T>[], on: boolean) => {
    const ids = cols.filter((c) => !c.pinned).map((c) => c.id)
    onChange(on ? hidden.filter((x) => !ids.includes(x)) : [...new Set([...hidden, ...ids])])
  }
  const reset = () => {
    onChange(defaults.filter((c) => c.hiddenByDefault && !c.pinned).map((c) => c.id))
    onOrderChange?.([])
  }
  return (
    <Popover.Root onOpenChange={(o) => !o && setQ('')}>
      <Popover.Trigger asChild>
        {compact ? (
          <IconButton size="sm" label={`${ar ? 'الأعمدة' : 'Columns'} (${visible})`} icon={Columns3} className={className} />
        ) : (
          <button type="button" className={cn('inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-surface px-3 text-xs font-semibold text-fg transition-colors hover:border-border-strong hover:bg-surface-hover', className)}>
            <Columns3 size={14} className="text-fg-subtle" aria-hidden />
            {ar ? 'الأعمدة' : 'Columns'}
            <span className="tabular rounded-full bg-bg-subtle px-1.5 py-0.5 text-2xs text-fg-muted">{formatNumber(visible, 'en')}</span>
          </button>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} collisionPadding={12} className="z-50 flex max-h-[min(72dvh,560px)] w-[min(92vw,380px)] flex-col overflow-hidden rounded-xl border border-border bg-surface-raised shadow-lg data-[state=open]:animate-scale-in">
          <div className="border-b border-border p-2">
            <label className="flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-2.5 focus-within:border-accent">
              <Search size={14} className="shrink-0 text-fg-subtle" aria-hidden />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={ar ? 'ابحث عن عمود' : 'Find a column'} className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-fg-subtle" />
              {q && <button type="button" onClick={() => setQ('')} aria-label={ar ? 'مسح' : 'Clear'} className="text-fg-subtle hover:text-fg"><X size={13} /></button>}
            </label>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
            {sections.map((sec) => {
              const cols = sec.cols.filter(match)
              if (!cols.length) return null
              const shown = sec.cols.filter((c) => c.pinned || !hidden.includes(c.id)).length
              return (
                <section key={sec.id} className="mb-1.5 last:mb-0">
                  {sec.title && (
                    <div className="flex items-start justify-between gap-2 px-2 pb-1 pt-2">
                      <div className="min-w-0">
                        <p className="text-2xs font-bold uppercase tracking-wide text-fg-muted">{sec.title}</p>
                        {sec.description && <p className="mt-0.5 text-2xs leading-snug text-fg-subtle">{sec.description}</p>}
                      </div>
                      <button type="button" onClick={() => setSection(sec.cols, shown < sec.cols.length)} className="shrink-0 text-2xs font-semibold text-brand-text hover:underline">
                        {shown < sec.cols.length ? (ar ? 'إظهار الكل' : 'Show all') : ar ? 'إخفاء الكل' : 'Hide all'}
                      </button>
                    </div>
                  )}
                  {cols.map((c, i) => {
                    const on = c.pinned || !hidden.includes(c.id)
                    const movable = reorderable && !c.pinned && !needle
                    const prev = cols[i - 1]
                    const next = cols[i + 1]
                    return (
                      <div
                        key={c.id}
                        draggable={movable}
                        onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', c.id) }}
                        onDragOver={(e) => { if (dragId && dragId !== c.id && !c.pinned && sec.cols.some((x) => x.id === dragId)) { e.preventDefault(); setOverId(c.id) } }}
                        onDragLeave={() => setOverId((o) => (o === c.id ? null : o))}
                        onDrop={(e) => { e.preventDefault(); if (dragId) moveTo(dragId, c.id); setDragId(null); setOverId(null) }}
                        onDragEnd={() => { setDragId(null); setOverId(null) }}
                        className={cn('group/row flex items-start gap-1 rounded-lg transition-colors', dragId === c.id && 'opacity-50', overId === c.id && 'bg-accent-soft ring-1 ring-accent/40')}
                      >
                      {movable && (
                        <span className="flex shrink-0 flex-col items-center pt-1 text-fg-subtle">
                          <span className="cursor-grab px-0.5 active:cursor-grabbing" aria-hidden title={ar ? 'اسحب لتغيير الترتيب' : 'Drag to reorder'}><DotsSixVertical size={14} /></span>
                        </span>
                      )}
                      <label className={cn('flex min-w-0 flex-1 items-start gap-2.5 rounded-lg px-2 py-1.5 text-sm', c.pinned ? 'text-fg-muted' : 'cursor-pointer hover:bg-surface-hover')}>
                        <span className="pt-0.5">{c.pinned ? <LockSimple size={15} className="text-fg-subtle" aria-label={ar ? 'ثابت' : 'Locked'} /> : <Checkbox checked={on} onChange={(v) => toggle(c.id, v)} />}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block leading-snug text-fg">{l(c.header)}</span>
                          {c.description && <span className="mt-0.5 block text-2xs leading-snug text-fg-subtle">{l(c.description)}</span>}
                        </span>
                      </label>
                      {movable && (
                        <span className="flex shrink-0 flex-col py-0.5">
                          <button type="button" disabled={!prev || Boolean(prev.pinned)} onClick={() => prev && moveTo(c.id, prev.id)} aria-label={ar ? `تحريك ${l(c.header)} لأعلى` : `Move ${l(c.header)} up`} className="rounded p-0.5 text-fg-subtle hover:bg-surface-hover hover:text-fg disabled:opacity-30"><CaretUp size={12} /></button>
                          <button type="button" disabled={!next} onClick={() => next && moveTo(c.id, next.id)} aria-label={ar ? `تحريك ${l(c.header)} لأسفل` : `Move ${l(c.header)} down`} className="rounded p-0.5 text-fg-subtle hover:bg-surface-hover hover:text-fg disabled:opacity-30"><CaretDown size={12} /></button>
                        </span>
                      )}
                      </div>
                    )
                  })}
                </section>
              )
            })}
            {sections.every((sec) => !sec.cols.some(match)) && <p className="px-3 py-6 text-center text-xs text-fg-muted">{ar ? 'لا يوجد عمود بهذا الاسم' : 'No column matches'}</p>}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-xs">
            <span className="tabular text-fg-muted">{ar ? `${formatNumber(visible, 'en')} من ${formatNumber(columns.length, 'en')} ظاهر` : `${formatNumber(visible, 'en')} of ${formatNumber(columns.length, 'en')} shown`}</span>
            <button type="button" onClick={reset} className="inline-flex items-center gap-1 font-semibold text-brand-text hover:underline"><ArrowCounterClockwise size={13} />{ar ? 'استعادة الوضع الافتراضي' : 'Reset to defaults'}</button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

/**
 * Waybill codes in a table cell: the first one as a quiet mono chip and «+N» for the rest, the whole list on hover —
 * instead of every code run together and cut off (owner, 2026-10-20).
 */
export function AwbList({ awbs, max = 1 }: { awbs: readonly string[]; max?: number }) {
  const list = awbs.filter(Boolean)
  if (!list.length) return <span className="text-fg-subtle/50">—</span>
  const shown = list.slice(0, max)
  const more = list.length - shown.length
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5" dir="ltr" title={list.join('\n')}>
      {shown.map((a) => <span key={a} className="truncate rounded-md bg-bg-subtle px-1.5 py-0.5 font-mono text-[12px] text-fg">{a}</span>)}
      {more > 0 && <span className="shrink-0 rounded-full bg-brand-soft px-1.5 py-0.5 text-[11px] font-bold text-brand-text">+{more}</span>}
    </span>
  )
}